import { IJob, JobState } from '@biolibtech/biolib-js/src';
import { ExperimentView, IResult, ResultState } from '../components/types';
import { IResultSensitivityLabel } from '../../../apiHooks/sensitivityLabelApi';
import { computeNodeSystemExceptionDict } from '@biolibtech/biolib-js/src/AppClient/remote.types';

export const ensureStringStartsWithSlash = (str: string): string => (str.startsWith('/') ? str : `/${str}`);

export function convertJobToResult(
    job: IJob & { sensitivity_labels?: IResultSensitivityLabel[] },
    userUuid: string | undefined,
    presigned_output_storage_url: string,
): IResult {
    let state: ResultState;
    switch (job.state) {
        case JobState.completed: {
            state = ResultState.completed;
            break;
        }
        case JobState.failed: {
            state = ResultState.error;
            break;
        }
        case JobState.inProgress: {
            state = ResultState.running;
            break;
        }
        case JobState.cancelled: {
            state = ResultState.cancelled;
            break;
        }
        default: {
            state = ResultState.unknown;
            break;
        }
    }

    return {
        state,
        allowedRemoteHosts: job.app_version.remote_hosts.map(({ hostname }) => hostname),
        linkableRemoteHosts: (job.app_version.linkable_remote_hosts ?? job.app_version.remote_hosts).map(
            ({ hostname }) => hostname,
        ),
        appUri: job.app_uri,
        authToken: job.auth_token,
        finishedAt: job.ended_at,
        isOwnedByUser: job.user_id === userUuid && !!userUuid,
        isResultAvailable: job.main_result?.state === 'ready',
        mainOutputFilePath: !job.app_version.main_output_file
            ? null
            : ensureStringStartsWithSlash(job.app_version.main_output_file),
        name: job.main_result?.name ?? '',
        noteCount: job.note_count,
        presigned_output_storage_url,
        shareableLink: `${job.sharing_base_url ?? location.origin}/results/${job.public_id}/?token=${job.auth_token}`,
        created_at: job.created_at,
        uuid: job.public_id,
        can_access_all_results_of_user: job.can_access_all_results_of_user ?? false,
        can_access_all_index_tables_of_user: job.can_access_all_index_tables_of_user ?? false,
        can_access_all_data_records_of_user: job.can_access_all_data_records_of_user ?? false,
        can_access_oauth_token_of_user: job.can_access_oauth_token_of_user ?? false,
        error_code: job.cloud_job?.error_code,
        requested_machine: job.requested_machine,
        main_output_port: job.app_version.main_output_port ?? null,
        requires_user_identity: job.app_version.requires_user_identity ?? false,
        results_proxy_hostname: job.app_version.results_proxy_hostname,
        visualization_prefer_full_width: job.visualization_prefer_full_width ?? false,
        send_notifications_to_user: job.send_notifications_to_user,
        sensitivityLabels: job.sensitivity_labels ?? [],
    };
}

export const loadingResult: IResult = {
    allowedRemoteHosts: [],
    linkableRemoteHosts: [],
    appUri: '',
    authToken: '',
    finishedAt: '',
    isOwnedByUser: false,
    isResultAvailable: false,
    mainOutputFilePath: null,
    name: '',
    noteCount: 0,
    presigned_output_storage_url: '',
    shareableLink: '',
    created_at: '',
    state: ResultState.unknown,
    uuid: null,
    can_access_all_results_of_user: false,
    can_access_all_index_tables_of_user: false,
    can_access_all_data_records_of_user: false,
    can_access_oauth_token_of_user: false,
    error_code: undefined,
    main_output_port: null,
    results_proxy_hostname: undefined,
    visualization_prefer_full_width: false,
    sensitivityLabels: [],
};

export function getCloudJobErrorMessage(errorCode?: number): string | null {
    if (!errorCode) {
        return null;
    }
    return computeNodeSystemExceptionDict[errorCode] ?? 'Unknown cloud error';
}

export function getExperimentViewFromPath(experimentUuid: string, path: string): ExperimentView | null {
    for (const view of Object.values(ExperimentView)) {
        if (path.startsWith(`/experiments/${experimentUuid}/${view}`)) {
            return view as ExperimentView;
        }
    }
    return null;
}
